import * as React from 'react';
import moment = require('moment');
import propz from 'propz';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';

type Props = {
  programme: AfterSchoolClubProgramme;
  status: string;
  onOpenBookingsManager: () => void;
};

const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {children}
  </svg>
);

const formatDate = (value: any): string => {
  if (!value) return 'Not set';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('D MMM YYYY, HH:mm') : 'Not set';
};

const getSelectionMode = (programme: AfterSchoolClubProgramme): string => {
  const mode = String(propz.get(programme, ['selectionRules', 'mode'], 'SINGLE') || 'SINGLE').toUpperCase();
  if (mode !== 'PRIORITY') return 'Single choice';

  const min = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'min'], 1));
  const max = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'max'], 10));
  return `Priority ${Number.isFinite(min) ? min : 1}–${Number.isFinite(max) ? max : 10}`;
};

const getPreBookingLabel = (programme: AfterSchoolClubProgramme): string => {
  const value = String(propz.get(programme, ['booking', 'preBookingAccess'], 'VIEW') || 'VIEW').toUpperCase();
  if (value === 'HIDE') return 'Hidden before opening';
  if (value === 'DRAFT') return 'Draft booking allowed';
  return 'Programme visible before opening';
};

export const ClubProgrammeBookingsTab: React.FC<Props> = ({ programme, status, onOpenBookingsManager }) => {
  const normalizedStatus = String(status || 'DRAFT').toUpperCase();
  const isCompleted = normalizedStatus === 'COMPLETED';
  const isDraft = normalizedStatus === 'DRAFT';
  const opensAt = propz.get(programme, ['booking', 'opensAt']);
  const closesAt = propz.get(programme, ['booking', 'closesAt']);
  const allowChanges = Boolean(propz.get(programme, ['booking', 'allowChangesAfterSubmit'], false));
  const clubs = Array.isArray((programme as any).clubs) ? (programme as any).clubs : [];

  return (
    <section className="cpProgrammeBooking">
      <div className="cpProgrammeBookingHeader">
        <span className="cpProgrammeBookingIcon">
          <Icon>
            <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
            <path d="M8 2v4M16 2v4M3 9h18M8 13h3M8 16h6" />
          </Icon>
        </span>
        <div>
          <div className="cpProgrammeBookingTitleRow">
            <h2>Programme booking</h2>
            <span className={`cpProgrammeBookingState is${isCompleted ? 'Completed' : isDraft ? 'Draft' : 'Active'}`}>
              {isCompleted ? 'Completed' : isDraft ? 'Not open' : 'In progress'}
            </span>
          </div>
          <p>
            {isCompleted
              ? 'Review confirmed allocations and the final booking outcome.'
              : 'Review bookings, resolve conflicts and confirm club allocations.'}
          </p>
        </div>
      </div>

      <div className="cpProgrammeBookingWindow">
        <div className="cpProgrammeBookingWindowTitle">
          <span>Booking window</span>
          <strong>{opensAt && closesAt ? 'Scheduled' : 'Incomplete'}</strong>
        </div>
        <div className="cpProgrammeBookingDates">
          <div>
            <span>Opens</span>
            <strong>{formatDate(opensAt)}</strong>
          </div>
          <span className="cpProgrammeBookingArrow" aria-hidden="true">
            <Icon>
              <path d="M5 12h14M14 7l5 5-5 5" />
            </Icon>
          </span>
          <div>
            <span>Closes</span>
            <strong>{formatDate(closesAt)}</strong>
          </div>
        </div>
      </div>

      <div className="cpProgrammeBookingConfig">
        <div>
          <span>Selection</span>
          <strong>{getSelectionMode(programme)}</strong>
        </div>
        <div>
          <span>Pre-booking</span>
          <strong>{getPreBookingLabel(programme)}</strong>
        </div>
        <div>
          <span>Changes after submission</span>
          <strong>{allowChanges ? 'Allowed' : 'Not allowed'}</strong>
        </div>
        <div>
          <span>Programme clubs</span>
          <strong>{clubs.length ? `${clubs.length} club${clubs.length === 1 ? '' : 's'}` : 'No clubs added'}</strong>
        </div>
      </div>

      <div className="cpProgrammeBookingWorkflow">
        <h3>{isCompleted ? 'Review workflow' : 'Allocation workflow'}</h3>
        <div>
          <span>1</span>
          <p>
            <strong>Review bookings</strong>
            <small>Check requests by day and club.</small>
          </p>
        </div>
        <div>
          <span>2</span>
          <p>
            <strong>{isCompleted ? 'Review conflicts' : 'Resolve conflicts'}</strong>
            <small>Identify students booked into overlapping clubs.</small>
          </p>
        </div>
        <div>
          <span>3</span>
          <p>
            <strong>{isCompleted ? 'View outcomes' : 'Confirm outcomes'}</strong>
            <small>Review club places and waiting-list allocations.</small>
          </p>
        </div>
      </div>

      <button type="button" className="cpProgrammeBookingButton" onClick={onOpenBookingsManager}>
        <Icon>
          <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
          <path d="M8 9h8M8 13h8M8 17h5" />
        </Icon>
        {isCompleted ? 'Open bookings review' : 'Open bookings manager'}
      </button>
    </section>
  );
};

ClubProgrammeBookingsTab.displayName = 'ClubProgrammeBookingsTab';
